import { ArrowLeft, CirclePlus, SquareX } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useToast } from "./components/ToastContext";
import { getUserMessage } from "./services/api";

const PersonalInfoScreen = ({
  userData,
  setUserData,
  userEmail,
  resumeData,
  setResumeData,
  setResumeExists,
  navigateTo,
  handleSaveResumeData,
  refreshMatchScores,
  setIsRefreshingScores,
  navigateBack,
  handleErrors,
}) => {
  const toast = useToast();
  const maxEducationEntries = 3;
  const [educationsToAdd, setEducationsToAdd] = React.useState(
    resumeData.education
      ? maxEducationEntries - resumeData.education.length
      : maxEducationEntries,
  );

  const maxExperienceEntries = 4;
  const [experiencesToAdd, setExperiencesToAdd] = React.useState(
    resumeData.work_experience
      ? maxExperienceEntries - resumeData.work_experience.length
      : maxExperienceEntries,
  );
  const maxSkillCategoryEntries = 5;
  const [editedCategories, setEditedCategories] = useState({});
  const [skillCategoriesToAdd, setSkillCategoriesToAdd] = React.useState(
    maxSkillCategoryEntries,
  );
  const maxProjectEntries = 4;
  const [projectsToAdd, setProjectsToAdd] = React.useState(
    resumeData.projects
      ? maxProjectEntries - resumeData.projects.length
      : maxProjectEntries,
  );
  const maxPublicationEntries = 4;
  const [publicationsToAdd, setPublicationsToAdd] = React.useState(
    resumeData.publications
      ? maxPublicationEntries - resumeData.publications.length
      : maxPublicationEntries,
  );

  const [validationErrors, setValidationErrors] = useState({
    country_code: false,
    phone: false,
  });

  const [tempSkillInputs, setTempSkillInputs] = useState({});
  const initialResumeName =
    resumeData.metadata?.name?.split(".")[0]?.trim() || "Resume";
  const [resumeSaveName, setResumeSaveName] = useState(initialResumeName);

  const cleanResumeSaveName = (name) =>
    (name || "")
      .trim()
      .replace(/\.[^/.]+$/, "")
      .replace(/[.$[\]#/\\]/g, "_")
      .replace(/\s+/g, " ");

  // Add this state initialization:
  useEffect(() => {
    if (resumeData.skills && typeof resumeData.skills === "object") {
      const initialTempInputs = {};
      Object.keys(resumeData.skills).forEach((category) => {
        initialTempInputs[category] = resumeData.skills[category].join(", ");
      });
      setTempSkillInputs(initialTempInputs);
    }
  }, []);

  const handleContinue = async () => {
    try {
      const requiredFields = [
        "first_name",
        "last_name",
        "email",
        "countryCode",
        "phone",
        "workAuthorization",
      ];
      const missingFields = requiredFields.filter(
        (field) => !resumeData.contact?.[field],
      );

      const errorsExist = Object.values(validationErrors).some(
        (v) => v === true,
      );

      if (missingFields.length > 0) {
        toast.error(
          `Please fill in required fields: ${missingFields.join(", ")}`,
        );
        return;
      }
      if (errorsExist) {
        toast.error("Some fields have invalid input. Please fix them.");
        return;
      }

      const cleanName = cleanResumeSaveName(resumeSaveName);
      if (!cleanName) {
        toast.error("Please enter a resume name.");
        return;
      }

      const resumeToSave = {
        ...resumeData,
        metadata: {
          ...resumeData.metadata,
          name: cleanName,
        },
      };
      const saved = await handleSaveResumeData(
        resumeToSave,
        initialResumeName !== cleanName ? initialResumeName : "",
      );
      if (!saved) {
        toast.error("We couldn't save your information. Please try again.");
        return;
      }
      const nextResumeData = {
        ...(userData.resumeData || {}),
        [cleanName]: resumeToSave,
      };
      if (initialResumeName !== cleanName) {
        delete nextResumeData[initialResumeName];
      }
      setResumeData(resumeToSave);
      setUserData({
        ...userData,
        resumeData: nextResumeData,
      });

      navigateTo("home");
      setResumeExists(true);
      await handleRefreshMatchScores();
    } catch (error) {
      handleErrors("Saving Personal Info", error);
      toast.error(getUserMessage(error));
    }
  };

  const handleAddEducation = async () => {
    if (educationsToAdd <= 0) return;
    setResumeData({
      ...resumeData,
      education: [...(resumeData.education || []), {}],
    });
    setEducationsToAdd(educationsToAdd - 1);
  };

  const handleDeleteEducation = async (edu) => {
    setResumeData({
      ...resumeData,
      education: resumeData.education.filter((e) => e !== edu),
    });
    setEducationsToAdd(educationsToAdd + 1);
  };

  const handleAddWorkExperience = async () => {
    if (experiencesToAdd <= 0) return;
    setResumeData({
      ...resumeData,
      work_experience: [...(resumeData.work_experience || []), {}],
    });
    setExperiencesToAdd(experiencesToAdd - 1);
  };

  const handleDeleteWorkExperience = async (exp) => {
    setResumeData({
      ...resumeData,
      work_experience: resumeData.work_experience.filter((e) => e !== exp),
    });
    setExperiencesToAdd(experiencesToAdd + 1);
  };

  const handleAddProject = async () => {
    if (projectsToAdd <= 0) return;
    setResumeData({
      ...resumeData,
      projects: [...(resumeData.projects || []), {}],
    });
    setProjectsToAdd(projectsToAdd - 1);
  };

  const handleDeleteProject = async (exp) => {
    setResumeData({
      ...resumeData,
      projects: resumeData.projects.filter((e) => e !== exp),
    });
    setProjectsToAdd(projectsToAdd + 1);
  };

  const handleAddPublication = async () => {
    if (publicationsToAdd <= 0) return;
    setResumeData({
      ...resumeData,
      publications: [...(resumeData.publications || []), {}],
    });
    setPublicationsToAdd(publicationsToAdd - 1);
  };

  const handleDeletePublication = async (publication) => {
    setResumeData({
      ...resumeData,
      publications: resumeData.publications.filter((p) => p !== publication),
    });
    setPublicationsToAdd(publicationsToAdd + 1);
  };

  const handleAddSkillCategory = () => {
    if (skillCategoriesToAdd <= 0) return;
    const newKey = `category_${Object.keys(resumeData.skills).length}`; // unique key
    setResumeData({
      ...resumeData,
      skills: { ...resumeData.skills, [newKey]: [] },
    });
    setEditedCategories((prev) => ({ ...prev, [newKey]: "New Category" }));
    setSkillCategoriesToAdd(skillCategoriesToAdd - 1);
  };

  const handleDeleteSkillCategory = (skill_category) => {
    const updated = { ...resumeData.skills };
    delete updated[skill_category];
    setResumeData({ ...resumeData, skills: updated });
    setEditedCategories((prev) => {
      const next = { ...prev };
      delete next[skill_category];
      return next;
    });
    setSkillCategoriesToAdd(skillCategoriesToAdd + 1);
  };

  const handleRefreshMatchScores = async () => {
    try {
      setIsRefreshingScores(true);
      await refreshMatchScores();
    } catch (error) {
      await handleErrors("Unable to refresh jobs", error);
      toast.error(getUserMessage(error));
    } finally {
      setIsRefreshingScores(false);
    }
  };

  const validatePhoneNumber = (e) => {
    const phone_num = e.target.value;
    setResumeData({
      ...resumeData,
      contact: {
        ...resumeData.contact,
        phone: phone_num,
      },
    });
    if (
      phone_num.length > 13 ||
      /[a-zA-Z]/.test(phone_num) ||
      /[^0-9+\-() ]/.test(phone_num)
    ) {
      setValidationErrors({ ...validationErrors, phone: true });
    } else {
      const { phone, ...rest } = validationErrors;
      setValidationErrors(rest);
    }
  };

  const validateEmail = (e) => {
    const resumeEmail = e.target.value;
    setResumeData({
      ...resumeData,
      contact: {
        ...resumeData.contact,
        email: resumeEmail,
      },
    });
    if (
      resumeEmail.length < 7 ||
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(resumeEmail)
    ) {
      setValidationErrors({ ...validationErrors, email: true });
    } else {
      const { email, ...rest } = validationErrors;
      setValidationErrors(rest);
    }
  };

  const validateFirstName = (e) => {
    const firstName = e.target.value;
    setResumeData({
      ...resumeData,
      contact: {
        ...resumeData.contact,
        first_name: firstName,
      },
    });
    if (!/^[a-zA-Z\s'-]+$/.test(firstName)) {
      setValidationErrors({ ...validationErrors, first_name: true });
    } else {
      const { first_name, ...rest } = validationErrors;
      setValidationErrors(rest);
    }
  };

  const validateLastName = (e) => {
    const lastName = e.target.value;
    setResumeData({
      ...resumeData,
      contact: {
        ...resumeData.contact,
        last_name: e.target.value,
      },
    });
    if (!/^[a-zA-Z\s'-]+$/.test(lastName)) {
      setValidationErrors({ ...validationErrors, last_name: true });
    } else {
      const { last_name, ...rest } = validationErrors;
      setValidationErrors(rest);
    }
  };

  const validateLinkedIn = (e) => {
    const linkedinUrl = e.target.value;
    setResumeData({
      ...resumeData,
      contact: {
        ...resumeData.contact,
        linkedin: linkedinUrl,
      },
    });
    if (
      !/^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[a-zA-Z0-9\-_%]+\/?$/.test(
        linkedinUrl,
      )
    ) {
      setValidationErrors({ ...validationErrors, linkedin: true });
    } else {
      const { linkedin, ...rest } = validationErrors;
      setValidationErrors(rest);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="mb-6">
            <h2 className="applicant-theme-text">
              Complete Your Resume Information
            </h2>
            <p className="text-gray-600">
              We've pre-filled what we found in your resume. Please review and
              add any information you would want on your resume.
            </p>
          </div>

          <div className="mb-6 max-w-xl">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Save it as:
            </label>
            <input
              type="text"
              value={resumeSaveName}
              onChange={(e) => setResumeSaveName(e.target.value)}
              onBlur={() =>
                setResumeSaveName(
                  cleanResumeSaveName(resumeSaveName) || initialResumeName,
                )
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Resume name"
            />
            <p className="text-xs text-gray-500 mt-1">
              This name appears in your resume dropdown and saved applications.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                Contact Information
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={resumeData.contact?.first_name || ""}
                  onChange={(e) => validateFirstName(e)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter your first name"
                />
                {validationErrors.first_name && (
                  <p className="text-xs text-red-600 mt-1">
                    Invalid First Name
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={resumeData.contact?.last_name || ""}
                  onChange={(e) => validateLastName(e)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter your last name"
                />
                {validationErrors.last_name && (
                  <p className="text-xs text-red-600 mt-1">Invalid Last Name</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={resumeData.contact?.email || ""}
                  onChange={(e) => validateEmail(e)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter your email address"
                />
                {validationErrors.email && (
                  <p className="text-xs text-red-600 mt-1">Invalid Email</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country Code *
                </label>
                <input
                  type="tel"
                  value={resumeData.contact?.countryCode || ""}
                  minLength={1}
                  maxLength={3}
                  onChange={(e) =>
                    setResumeData({
                      ...resumeData,
                      contact: {
                        ...resumeData.contact,
                        countryCode: e.target.value,
                      },
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter your country code (e.g. +1)"
                />
                {validationErrors.country_code && (
                  <p className="text-xs text-red-600 mt-1">
                    Invalid Country Code
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={resumeData.contact?.phone || ""}
                  minLength={7}
                  maxLength={14}
                  onChange={(e) => validatePhoneNumber(e)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter your phone number"
                />
                {validationErrors.phone && (
                  <p className="text-xs text-red-600 mt-1">
                    Invalid Phone Number
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={resumeData.contact?.location || ""}
                  onChange={(e) =>
                    setResumeData({
                      ...resumeData,
                      contact: {
                        ...resumeData.contact,
                        location: e.target.value,
                      },
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="City, State"
                />
                {validationErrors.location && (
                  <p className="text-xs text-red-600 mt-1">Invalid Location</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  LinkedIn Profile
                </label>
                <input
                  type="url"
                  value={resumeData.contact?.linkedin || ""}
                  onChange={(e) => validateLinkedIn(e)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="https://linkedin.com/in/yourprofile"
                />
                {validationErrors.linkedin && (
                  <p className="text-xs text-red-600 mt-1">
                    Invalid LinkedIn URL
                  </p>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-2">
                    Work Experiences
                  </h2>
                  <div>
                    {resumeData.work_experience?.map((exp, index) => (
                      <div key={index} className="mb-4 border-b pb-4">
                        {index > 0 && <div className="horizontal-line"></div>}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Position
                          </label>
                          <input
                            type="text"
                            value={exp.title || ""}
                            onChange={(e) => {
                              const updated = [
                                ...(resumeData.work_experience || []),
                              ];
                              updated[index] = {
                                ...updated[index],
                                title: e.target.value,
                              };
                              setResumeData({
                                ...resumeData,
                                work_experience: updated,
                              });
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="e.g. Software Engineer"
                          />
                          {exp.title && (
                            <p className="text-xs text-green-600 mt-1">
                              ✓ Parsed from resume
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Company Name
                          </label>
                          <input
                            type="text"
                            value={exp.company || ""}
                            onChange={(e) => {
                              const updated = [
                                ...(resumeData.work_experience || []),
                              ];
                              updated[index] = {
                                ...updated[index],
                                company: e.target.value,
                              };
                              setResumeData({
                                ...resumeData,
                                work_experience: updated,
                              });
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="e.g. Tech Corp"
                          />
                          {exp.company && (
                            <p className="text-xs text-green-600 mt-1">
                              ✓ Parsed from resume
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Location
                          </label>
                          <input
                            type="text"
                            value={exp.location || ""}
                            onChange={(e) => {
                              const updated = [
                                ...(resumeData.work_experience || []),
                              ];
                              updated[index] = {
                                ...updated[index],
                                location: e.target.value,
                              };
                              setResumeData({
                                ...resumeData,
                                work_experience: updated,
                              });
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="City, State"
                          />
                          {exp.location && (
                            <p className="text-xs text-green-600 mt-1">
                              ✓ Parsed from resume
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Start month
                            </label>
                            <select
                              value={exp.start_month || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...(resumeData.work_experience || []),
                                ];
                                updated[index] = {
                                  ...updated[index],
                                  start_month: e.target.value,
                                };
                                setResumeData({
                                  ...resumeData,
                                  work_experience: updated,
                                });
                              }}
                              className="w-half px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            >
                              <option value="">Month</option>
                              <option value="01">Jan</option>
                              <option value="02">Feb</option>
                              <option value="03">Mar</option>
                              <option value="04">Apr</option>
                              <option value="05">May</option>
                              <option value="06">Jun</option>
                              <option value="07">Jul</option>
                              <option value="08">Aug</option>
                              <option value="09">Sep</option>
                              <option value="10">Oct</option>
                              <option value="11">Nov</option>
                              <option value="12">Dec</option>
                            </select>
                            {exp.start_month && (
                              <p className="text-xs text-green-600 mt-1">
                                ✓ Parsed from resume
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Start year
                            </label>
                            <input
                              type="number"
                              value={exp.start_year || ""}
                              min="1950"
                              max={new Date().getFullYear()}
                              onChange={(e) => {
                                const updated = [
                                  ...(resumeData.work_experience || []),
                                ];
                                updated[index] = {
                                  ...updated[index],
                                  start_year: e.target.value,
                                };
                                setResumeData({
                                  ...resumeData,
                                  work_experience: updated,
                                });
                              }}
                              className="w-half px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              placeholder="yyyy"
                            />
                            {exp.start_year && (
                              <p className="text-xs text-green-600 mt-1">
                                ✓ Parsed from resume
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              End month
                            </label>
                            <select
                              value={exp.end_month || ""}
                              onChange={(e) => {
                                const updated = [
                                  ...(resumeData.work_experience || []),
                                ];
                                updated[index] = {
                                  ...updated[index],
                                  end_month: e.target.value,
                                };
                                setResumeData({
                                  ...resumeData,
                                  work_experience: updated,
                                });
                              }}
                              className="w-half px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            >
                              <option value="">Month</option>
                              <option value="01">Jan</option>
                              <option value="02">Feb</option>
                              <option value="03">Mar</option>
                              <option value="04">Apr</option>
                              <option value="05">May</option>
                              <option value="06">Jun</option>
                              <option value="07">Jul</option>
                              <option value="08">Aug</option>
                              <option value="09">Sep</option>
                              <option value="10">Oct</option>
                              <option value="11">Nov</option>
                              <option value="12">Dec</option>
                            </select>
                            {exp.end_month && (
                              <p className="text-xs text-green-600 mt-1">
                                ✓ Parsed from resume
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              End year
                            </label>
                            <input
                              type="number"
                              value={exp.end_year || ""}
                              min="1950"
                              max={new Date().getFullYear()}
                              onChange={(e) => {
                                const updated = [
                                  ...(resumeData.work_experience || []),
                                ];
                                updated[index] = {
                                  ...updated[index],
                                  end_year: e.target.value,
                                };
                                setResumeData({
                                  ...resumeData,
                                  work_experience: updated,
                                });
                              }}
                              className="w-half px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              placeholder="yyyy"
                            />
                            {exp.end_year && (
                              <p className="text-xs text-green-600 mt-1">
                                ✓ Parsed from resume
                              </p>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Responsibilities
                          </label>
                          <textarea
                            value={exp.responsibilities?.join("; ") || ""}
                            onChange={(e) => {
                              const updated = [
                                ...(resumeData.work_experience || []),
                              ];
                              updated[index] = {
                                ...updated[index],
                                responsibilities: e.target.value.split("; "),
                              };
                              setResumeData({
                                ...resumeData,
                                work_experience: updated,
                              });
                            }}
                            rows="4"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="Separate responsibilities with semicolons"
                          />
                          {exp.responsibilities && (
                            <p className="text-xs text-green-600 mt-1">
                              ✓ Parsed from resume
                            </p>
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            className="buttons-red-mini px-2 m-sm"
                            type="button"
                            onClick={() => handleDeleteWorkExperience(exp)}
                          >
                            <SquareX className="min-w-2 min-h-2" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {experiencesToAdd > 0 && (
                    <div>
                      <button
                        onClick={handleAddWorkExperience}
                        className="buttons-indigo"
                      >
                        <CirclePlus className="w-4 h-4" />
                      </button>
                      <p className="text-gray-600 text-xs">
                        Add up to {experiencesToAdd} more work experience
                        entries
                      </p>
                    </div>
                  )}
                </div>
                <div className="horizontal-line"></div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-2">
                    Skills
                  </h2>
                  {Object.entries(resumeData.skills || {}).map(
                    ([category, skillsList]) => (
                      <div
                        key={category}
                        className="grid grid-cols-1 md:grid-cols-2 buttons-container"
                      >
                        <textarea
                          value={editedCategories[category] ?? category}
                          onChange={(e) => {
                            // Update display value immediately
                            setEditedCategories((prev) => ({
                              ...prev,
                              [category]: e.target.value,
                            }));
                          }}
                          onBlur={(e) => {
                            // Only update resumeData when user finishes editing (on blur)
                            const newKey = e.target.value
                              .trim()
                              .replace(/\s+/g, "_")
                              .toLowerCase();
                            const updated = Object.fromEntries(
                              Object.entries(resumeData.skills).map(([k, v]) =>
                                k === category ? [newKey, v] : [k, v],
                              ),
                            );
                            setResumeData({ ...resumeData, skills: updated });
                            setEditedCategories((prev) => {
                              const next = { ...prev };
                              delete next[category];
                              next[newKey] = e.target.value.trim();
                              return next;
                            });
                          }}
                          className="w-half px-4 py-3 border border-gray-300 rounded-lg"
                        />
                        <textarea
                          value={
                            tempSkillInputs[category] || skillsList.join(", ")
                          }
                          onChange={(e) => {
                            // Update temporary input state
                            setTempSkillInputs({
                              ...tempSkillInputs,
                              [category]: e.target.value,
                            });
                          }}
                          onBlur={(e) => {
                            // Process and save when focus leaves
                            const rawValue = e.target.value;
                            const processedSkills = rawValue
                              .split(",")
                              .map((s) => s.trim())
                              .filter((s) => s !== "");

                            setResumeData({
                              ...resumeData,
                              skills: {
                                ...resumeData.skills,
                                [category]: processedSkills,
                              },
                            });
                          }}
                          placeholder="e.g., Python, JavaScript, AWS"
                          className="w-half px-4 py-3 border border-gray-300 rounded-lg"
                        />
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            className="buttons-red-mini px-2 m-sm"
                            type="button"
                            onClick={() => handleDeleteSkillCategory(category)}
                          >
                            <SquareX className="min-w-2 min-h-2" />
                          </button>
                        </div>
                      </div>
                    ),
                  )}

                  {resumeData.skills && (
                    <p className="text-xs text-green-600 mt-1">
                      ✓ Parsed from resume
                    </p>
                  )}
                  {skillCategoriesToAdd > 0 && (
                    <div>
                      <button
                        onClick={handleAddSkillCategory}
                        className="buttons-indigo"
                      >
                        <CirclePlus className="w-4 h-4" />
                      </button>
                      <p className="text-gray-600 text-xs">
                        Add up to {skillCategoriesToAdd} more skills entries
                      </p>
                    </div>
                  )}
                </div>
                <div className="horizontal-line"></div>

                <div>
                  <h2 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-2">
                    Interests
                  </h2>
                  <textarea
                    value={
                      resumeData.interests
                        ? Object.values(resumeData.interests).flat().join(", ")
                        : ""
                    }
                    onChange={(e) =>
                      setResumeData({
                        ...resumeData,
                        interests: e.target.value.split(", "),
                      })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="e.g. Hiking, Reading, Traveling"
                    rows="4"
                  />
                  {resumeData.interests && (
                    <p className="text-xs text-green-600 mt-1">
                      ✓ Parsed from resume
                    </p>
                  )}
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-2">
                    Work Authorization *
                  </h2>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="workAuth"
                        checked={
                          resumeData.contact?.workAuthorization === "authorized"
                        }
                        onChange={() =>
                          setResumeData({
                            ...resumeData,
                            contact: {
                              ...resumeData.contact,
                              workAuthorization: "authorized",
                            },
                          })
                        }
                        className="rounded border-gray-300 text-indigo-600 mr-2"
                      />
                      <span className="text-sm text-gray-700">
                        I am authorized to work in the US
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="workAuth"
                        checked={
                          resumeData.contact?.workAuthorization ===
                          "need_sponsorship"
                        }
                        onChange={() =>
                          setResumeData({
                            ...resumeData,
                            contact: {
                              ...resumeData.contact,
                              workAuthorization: "need_sponsorship",
                            },
                          })
                        }
                        className="rounded border-gray-300 text-indigo-600 mr-2"
                      />
                      <span className="text-sm text-gray-700">
                        I will need sponsorship
                      </span>
                    </label>
                  </div>
                </div>

                <div className="bg-indigo-50 rounded-lg p-4">
                  <h4 className="font-medium text-indigo-600 mb-2">
                    Resume Summary
                  </h4>
                  <textarea
                    value={resumeData.summary || ""}
                    onChange={(e) =>
                      setResumeData({
                        ...resumeData,
                        summary: e.target.value,
                      })
                    }
                    rows={4}
                    className="w-full px-3 py-2 border border-indigo-200 rounded-lg bg-white text-sm text-gray-800 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Add a resume summary"
                  />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                Additional Information
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Portfolio/Website
                </label>
                <input
                  type="url"
                  value={resumeData?.contact?.portfolio || ""}
                  onChange={(e) =>
                    setResumeData({
                      ...resumeData,
                      contact: {
                        ...resumeData.contact,
                        portfolio: e.target.value,
                      },
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="https://yourportfolio.com"
                />
                {resumeData.contact?.portfolio && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Parsed from resume
                  </p>
                )}
              </div>

              <div>
                <h2 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-2">
                  Education
                </h2>
                <div>
                  {resumeData.education?.map((edu, index) => (
                    <div key={index} className="mb-1">
                      {index > 0 && <div className="horizontal-line"></div>}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          School name
                        </label>
                        <input
                          type="text"
                          value={edu.school || ""}
                          onChange={(e) => {
                            const updated = [...(resumeData.education || [])];

                            updated[index] = {
                              ...updated[index],
                              school: e.target.value,
                            };

                            setResumeData({
                              ...resumeData,
                              education: updated,
                            });
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="e.g. Example University"
                        />
                        {edu.school && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Parsed from resume
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Degree Earned
                        </label>
                        <input
                          type="text"
                          value={edu.degree || ""}
                          onChange={(e) => {
                            const updated = [...(resumeData.education || [])];

                            updated[index] = {
                              ...updated[index],
                              degree: e.target.value,
                            };

                            setResumeData({
                              ...resumeData,
                              education: updated,
                            });
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="e.g. Bachelors in Science"
                        />
                        {edu.degree && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Parsed from resume
                          </p>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Start month
                          </label>
                          <select
                            value={edu.start_month || ""}
                            onChange={(e) => {
                              const updated = [...(resumeData.education || [])];

                              updated[index] = {
                                ...updated[index],
                                start_month: e.target.value,
                              };

                              setResumeData({
                                ...resumeData,
                                education: updated,
                              });
                            }}
                            className="w-half px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          >
                            <option value="">Month</option>
                            <option value="01">Jan</option>
                            <option value="02">Feb</option>
                            <option value="03">Mar</option>
                            <option value="04">Apr</option>
                            <option value="05">May</option>
                            <option value="06">Jun</option>
                            <option value="07">Jul</option>
                            <option value="08">Aug</option>
                            <option value="09">Sep</option>
                            <option value="10">Oct</option>
                            <option value="11">Nov</option>
                            <option value="12">Dec</option>
                          </select>
                          {edu.start_month && (
                            <p className="text-xs text-green-600 mt-1">
                              ✓ Parsed from resume
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Start year
                          </label>
                          <input
                            type="number"
                            value={edu.start_year || ""}
                            min="1950"
                            max={new Date().getFullYear()}
                            onChange={(e) => {
                              const updated = [...(resumeData.education || [])];

                              updated[index] = {
                                ...updated[index],
                                start_year: e.target.value,
                              };

                              setResumeData({
                                ...resumeData,
                                education: updated,
                              });
                            }}
                            className="w-half px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="yyyy"
                          />
                          {edu.start_year && (
                            <p className="text-xs text-green-600 mt-1">
                              ✓ Parsed from resume
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Graduation month
                          </label>
                          <select
                            value={edu.end_month || ""}
                            onChange={(e) => {
                              const updated = [...(resumeData.education || [])];

                              updated[index] = {
                                ...updated[index],
                                end_month: e.target.value,
                              };

                              setResumeData({
                                ...resumeData,
                                education: updated,
                              });
                            }}
                            className="w-half px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          >
                            <option value="">Month</option>
                            <option value="01">Jan</option>
                            <option value="02">Feb</option>
                            <option value="03">Mar</option>
                            <option value="04">Apr</option>
                            <option value="05">May</option>
                            <option value="06">Jun</option>
                            <option value="07">Jul</option>
                            <option value="08">Aug</option>
                            <option value="09">Sep</option>
                            <option value="10">Oct</option>
                            <option value="11">Nov</option>
                            <option value="12">Dec</option>
                          </select>
                          {edu.end_month && (
                            <p className="text-xs text-green-600 mt-1">
                              ✓ Parsed from resume
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Graduation year
                          </label>
                          <input
                            type="number"
                            min="1950"
                            max={new Date().getFullYear()}
                            value={edu.end_year || ""}
                            onChange={(e) => {
                              const updated = [...(resumeData.education || [])];

                              updated[index] = {
                                ...updated[index],
                                end_year: e.target.value,
                              };

                              setResumeData({
                                ...resumeData,
                                education: updated,
                              });
                            }}
                            className="w-half px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            placeholder="yyyy"
                          />
                          {edu.end_year && (
                            <p className="text-xs text-green-600 mt-1">
                              ✓ Parsed from resume
                            </p>
                          )}
                        </div>
                      </div>
                      <div
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <button
                          className="buttons-red-mini px-2 m-sm"
                          type="button"
                          onClick={() => handleDeleteEducation(edu)}
                        >
                          <SquareX className="min-w-2 min-h-2" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {educationsToAdd > 0 && (
                <div className="text-center">
                  <button
                    onClick={handleAddEducation}
                    className="buttons-indigo"
                  >
                    <CirclePlus className="w-4 h-4" />
                  </button>
                  <p className="text-gray-600 text-xs">
                    Add up to {educationsToAdd} more education entries
                  </p>
                </div>
              )}
              <div className="horizontal-line"></div>
              <div>
                {resumeData.projects && (
                  <h2 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-2">
                    Projects
                  </h2>
                )}
                <div>
                  {resumeData.projects?.map((project, index) => (
                    <div key={index} className="mb-4 border-b pb-4">
                      {index > 0 && <div className="horizontal-line"></div>}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name
                        </label>
                        <input
                          type="text"
                          value={project.name || ""}
                          onChange={(e) => {
                            const updated = [...(resumeData.projects || [])];
                            updated[index] = {
                              ...updated[index],
                              name: e.target.value,
                            };
                            setResumeData({
                              ...resumeData,
                              projects: updated,
                            });
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="e.g. Building a robot"
                        />
                        {project.name && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Parsed from resume
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Type
                        </label>
                        <input
                          type="text"
                          value={project.type || ""}
                          onChange={(e) => {
                            const updated = [...(resumeData.projects || [])];
                            updated[index] = {
                              ...updated[index],
                              type: e.target.value,
                            };
                            setResumeData({
                              ...resumeData,
                              projects: updated,
                            });
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="e.g. Course, Personal, or Company"
                        />
                        {project.type && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Parsed from resume
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Location
                        </label>
                        <input
                          type="text"
                          value={project.location || ""}
                          onChange={(e) => {
                            const updated = [...(resumeData.projects || [])];
                            updated[index] = {
                              ...updated[index],
                              location: e.target.value,
                            };
                            setResumeData({
                              ...resumeData,
                              projects: updated,
                            });
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="City, State"
                        />
                        {project.location && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Parsed from resume
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Project Link
                        </label>
                        <input
                          type="url"
                          value={project.url || ""}
                          onChange={(e) => {
                            const updated = [...(resumeData.projects || [])];
                            updated[index] = {
                              ...updated[index],
                              url: e.target.value,
                            };
                            setResumeData({
                              ...resumeData,
                              projects: updated,
                            });
                          }}
                          className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="e.g Project link, GitHub repo etc."
                        />
                        {project.url && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Parsed from resume
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Date
                        </label>
                        <input
                          type="month"
                          value={project.date || ""}
                          onChange={(e) => {
                            const updated = [...(resumeData.projects || [])];
                            updated[index] = {
                              ...updated[index],
                              date: e.target.value,
                            };
                            setResumeData({
                              ...resumeData,
                              projects: updated,
                            });
                          }}
                          className="w-half px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="mm"
                        />
                        {project.date && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Parsed from resume
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={project.description?.join("; ") || ""}
                          onChange={(e) => {
                            const updated = [...(resumeData.projects || [])];
                            updated[index] = {
                              ...updated[index],
                              description: e.target.value.split("; "),
                            };
                            setResumeData({
                              ...resumeData,
                              projects: updated,
                            });
                          }}
                          rows="4"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="Separate responsibilities with semicolons"
                        />
                        {project.description && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Parsed from resume
                          </p>
                        )}
                      </div>
                      <div
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <button
                          className="buttons-red-mini px-2 m-sm"
                          type="button"
                          onClick={() => handleDeleteProject(project)}
                        >
                          <SquareX className="min-w-2 min-h-2" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {projectsToAdd > 0 && (
                  <div className="text-center">
                    <button
                      onClick={handleAddProject}
                      className="buttons-indigo"
                    >
                      <CirclePlus className="w-4 h-4" />
                    </button>
                    <p className="text-gray-600 text-xs">
                      Add up to {projectsToAdd} more project entries
                    </p>
                  </div>
                )}
              </div>
              <div className="horizontal-line"></div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-2">
                  Publications
                </h2>
                <div>
                  {resumeData.publications?.map((publication, index) => (
                    <div key={index} className="mb-4 border-b pb-4">
                      {index > 0 && <div className="horizontal-line"></div>}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Title
                        </label>
                        <input
                          type="text"
                          value={publication.title || ""}
                          onChange={(e) => {
                            const updated = [
                              ...(resumeData.publications || []),
                            ];
                            updated[index] = {
                              ...updated[index],
                              title: e.target.value,
                            };
                            setResumeData({
                              ...resumeData,
                              publications: updated,
                            });
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="e.g. Learning representations for job matching"
                        />
                        {publication.title && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Parsed from resume
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Authors
                        </label>
                        <input
                          type="text"
                          value={publication.authors || ""}
                          onChange={(e) => {
                            const updated = [
                              ...(resumeData.publications || []),
                            ];
                            updated[index] = {
                              ...updated[index],
                              authors: e.target.value,
                            };
                            setResumeData({
                              ...resumeData,
                              publications: updated,
                            });
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="e.g. Jane Doe, John Smith"
                        />
                        {publication.authors && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Parsed from resume
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Journal or Venue
                        </label>
                        <input
                          type="text"
                          value={publication.journal || ""}
                          onChange={(e) => {
                            const updated = [
                              ...(resumeData.publications || []),
                            ];
                            updated[index] = {
                              ...updated[index],
                              journal: e.target.value,
                            };
                            setResumeData({
                              ...resumeData,
                              publications: updated,
                            });
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="e.g. ACM Conference on Applied Computing"
                        />
                        {publication.journal && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Parsed from resume
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Published Date
                        </label>
                        <input
                          type="text"
                          value={publication.published_date || ""}
                          onChange={(e) => {
                            const updated = [
                              ...(resumeData.publications || []),
                            ];
                            updated[index] = {
                              ...updated[index],
                              published_date: e.target.value,
                            };
                            setResumeData({
                              ...resumeData,
                              publications: updated,
                            });
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="e.g. 2024"
                        />
                        {publication.published_date && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ Parsed from resume
                          </p>
                        )}
                      </div>

                      <div
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <button
                          className="buttons-red-mini px-2 m-sm"
                          type="button"
                          onClick={() => handleDeletePublication(publication)}
                        >
                          <SquareX className="min-w-2 min-h-2" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {publicationsToAdd > 0 && (
                  <div className="text-center">
                    <button
                      onClick={handleAddPublication}
                      className="buttons-indigo"
                    >
                      <CirclePlus className="w-4 h-4" />
                    </button>
                    <p className="text-gray-600 text-xs">
                      Add up to {publicationsToAdd} more publication entries
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <button
              onClick={() => navigateTo("start")}
              className="buttons-indigo"
            >
              Try Another Resume
            </button>
            <button onClick={() => handleContinue()} className="buttons-green">
              Save & Continue
            </button>
          </div>
          <p className="text-gray-600 text-xs">
            This information will be used to tailor this resume. You can upload
            up to 3 resumes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoScreen;
